import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import '../styles/GestionInfos.css';
import AdminSidebar from '../components/AdminSidebar';
import LoginForm from '../components/LoginForm';
import './AdminPage.css';
import { clearAuth, getAuthRole, setAuthRole, subscribeAuthRole } from '../utils/auth';

const LANGUAGES = ['fr', 'it', 'en', 'de', 'es', 'pt', 'ru'];

const LANGUAGE_NAMES_FR = {
  fr: 'Français',
  it: 'Italien',
  en: 'Anglais',
  de: 'Allemand',
  es: 'Espagnol',
  pt: 'Portugais',
  ru: 'Russe'
};

const EMPTY_LANGUAGES = { fr: false, it: false, en: false, de: false, es: false, pt: false, ru: false };

const sanitizeSlug = (value) => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const GestionInfos = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://villa-santeodoro.com';
  const [baseUrl, setBaseUrl] = useState(origin);
  const [guides, setGuides] = useState([]);
  const [qrDataUrls, setQrDataUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [uploadingGuideId, setUploadingGuideId] = useState(null);
  const [uploadingLang, setUploadingLang] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return getAuthRole() === 'admin';
  });

  const [newGuide, setNewGuide] = useState({
    slug: '',
    label: '',
    languages: { ...EMPTY_LANGUAGES }
  });

  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: '',
    cancelLabel: 'Fermer',
    onConfirm: null,
    onClose: null,
    dangerConfirm: false
  });

  const openInfoModal = (title, message, onClose = null) => {
    setDialog({
      isOpen: true,
      title,
      message,
      confirmLabel: '',
      cancelLabel: 'Fermer',
      onConfirm: null,
      onClose,
      dangerConfirm: false
    });
  };

  const openConfirmModal = (title, message, onConfirm, options = {}) => {
    setDialog({
      isOpen: true,
      title,
      message,
      confirmLabel: options.confirmLabel || 'Confirmer',
      cancelLabel: options.cancelLabel || 'Annuler',
      onConfirm,
      onClose: null,
      dangerConfirm: Boolean(options.dangerConfirm)
    });
  };

  const closeDialog = () => {
    const afterClose = dialog.onClose;
    setDialog((previous) => ({ ...previous, isOpen: false, onClose: null }));
    if (afterClose) afterClose();
  };

  const handleDialogConfirm = () => {
    const confirmAction = dialog.onConfirm;
    closeDialog();
    if (confirmAction) {
      confirmAction();
    }
  };

  useEffect(() => {
    return subscribeAuthRole((role) => {
      setIsAuthenticated(role === 'admin');
    });
  }, []);

  const handleLogin = (role) => {
    if (role !== 'admin') {
      return;
    }
    setAuthRole('admin');
  };

  const handleLogout = () => {
    clearAuth();
  };

  const loadGuides = useCallback(async () => {
    try {
      setLoading(true);
      const endpoints = [
        `${origin}/api/guides/config.php`,
        'https://villa-santeodoro.com/api/guides/config.php'
      ];

      let lastError = null;
      let data = null;
      let resolvedBaseUrl = null;

      for (const endpoint of endpoints) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(endpoint, { cache: 'no-store' });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const contentType = response.headers.get('content-type') || '';
          if (!contentType.toLowerCase().includes('application/json')) {
            throw new Error('Réponse non JSON');
          }

          // eslint-disable-next-line no-await-in-loop
          data = await response.json();
          resolvedBaseUrl = new URL(endpoint).origin;
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!data) {
        throw lastError || new Error('Erreur lors du chargement des guides.');
      }

      if (resolvedBaseUrl) {
        setBaseUrl(resolvedBaseUrl);
      }

      setGuides(data.guides || []);
      setError(null);
    } catch (err) {
      setError(
        err?.message === 'Réponse non JSON'
          ? 'API Guides inaccessible (réponse HTML). Vérifiez que /api/guides/config.php est bien servi en PHP sur ce domaine.'
          : err.message
      );
      setGuides([]);
    } finally {
      setLoading(false);
    }
  }, [origin]);

  useEffect(() => {
    loadGuides();
  }, [loadGuides]);

  useEffect(() => {
    if (guides.length === 0) return;
    const generateAll = async () => {
      const urls = {};
      await Promise.all(
        guides.map(async (guide) => {
          try {
            urls[guide.id] = await QRCode.toDataURL(
              `${baseUrl}/guides/${guide.id}`,
              { width: 500, margin: 2 }
            );
          } catch (_) {}
        })
      );
      setQrDataUrls(urls);
    };
    generateAll();
  }, [guides, baseUrl]);

  const handleCreateGuide = async (e) => {
    e.preventDefault();
    const cleanedSlug = sanitizeSlug(newGuide.slug);
    if (cleanedSlug !== newGuide.slug) {
      setNewGuide((prev) => ({ ...prev, slug: cleanedSlug }));
    }

    if (!cleanedSlug || !newGuide.label.trim()) {
      openInfoModal('Champs requis', 'Veuillez remplir le slug et le nom du guide.');
      return;
    }

    if (!Object.values(newGuide.languages).some((v) => v)) {
      openInfoModal('Langues requises', 'Sélectionnez au moins une langue.');
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/api/guides/config.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          slug: cleanedSlug,
          label: newGuide.label.trim(),
          languages: newGuide.languages
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la création du guide.');
      }

      setNewGuide({ slug: '', label: '', languages: { ...EMPTY_LANGUAGES } });
      setIsCreateModalOpen(false);
      openInfoModal('Succès', 'Guide créé avec succès.', () => window.location.reload());
    } catch (err) {
      openInfoModal('Erreur', err.message);
    }
  };

  const handleUploadPDF = async (e, guideId, language) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (file.type !== 'application/pdf') {
      openInfoModal('Fichier invalide', 'Veuillez sélectionner un fichier PDF.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      openInfoModal('Fichier trop volumineux', 'La taille maximum autorisée est de 50 MB.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('guideId', guideId);
    formData.append('language', language);

    try {
      setUploadingGuideId(guideId);
      setUploadingLang(language);

      const response = await fetch(`${baseUrl}/api/guides/upload.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du PDF.');
      }

      openInfoModal('Succès', 'PDF téléchargé avec succès.', () => window.location.reload());
    } catch (err) {
      openInfoModal('Erreur', err.message);
    } finally {
      setUploadingGuideId(null);
      setUploadingLang(null);
      e.target.value = '';
    }
  };

  const handleDownloadQR = (guideId) => {
    const dataUrl = qrDataUrls[guideId];
    if (!dataUrl) {
      openInfoModal('Erreur', 'QR code non encore disponible.');
      return;
    }
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${guideId}-qrcode.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const performDeleteGuide = async (guideId) => {
    try {
      const response = await fetch(`${baseUrl}/api/guides/config.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          guideId
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du guide.');
      }

      openInfoModal('Succès', 'Guide supprimé avec succès.', () => window.location.reload());
    } catch (err) {
      openInfoModal('Erreur', err.message);
    }
  };

  const handleDeleteGuide = (guideId) => {
    openConfirmModal(
      'Supprimer le guide',
      'Êtes-vous sûr de vouloir supprimer ce guide ? Cette action est irréversible.',
      () => performDeleteGuide(guideId),
      { confirmLabel: 'Supprimer', dangerConfirm: true }
    );
  };

  const handleRegenerateQR = async (guideId) => {
    try {
      const dataUrl = await QRCode.toDataURL(
        `${baseUrl}/guides/${guideId}`,
        { width: 500, margin: 2 }
      );
      setQrDataUrls((prev) => ({ ...prev, [guideId]: dataUrl }));
      openInfoModal('Succès', 'QR code régénéré avec succès.');
    } catch (err) {
      openInfoModal('Erreur', `Impossible de générer le QR: ${err.message}`);
    }
  };

  const performDeletePDF = async (guideId, language) => {
    try {
      const response = await fetch(`${baseUrl}/api/guides/config.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'deletePdf',
          guideId,
          language
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du PDF.');
      }

      openInfoModal('Succès', 'PDF supprimé avec succès.', () => window.location.reload());
    } catch (err) {
      openInfoModal('Erreur', err.message);
    }
  };

  const handleDeletePDF = (guideId, language) => {
    openConfirmModal(
      'Supprimer le PDF',
      `Supprimer le PDF ${language.toUpperCase()} de ce guide ?`,
      () => performDeletePDF(guideId, language),
      { confirmLabel: 'Supprimer', dangerConfirm: true }
    );
  };

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="site-shell admin-shell">
      <AdminSidebar
        onLogout={handleLogout}
        sitePath="/fr/location-maison-appartement-sardaigne-san-teodoro"
        role="admin"
      />
      <main className="site-shell-content">
        <div className="gestion-container">
          <h1>Gestion des guides</h1>

          <button className="btn-primary" onClick={() => setIsCreateModalOpen(true)}>
            + Créer un nouveau guide
          </button>

          {loading && <p>Chargement...</p>}
          {error && <div className="error-message">{error}</div>}

          {!loading && guides.length === 0 ? (
            <p className="no-guides">Aucun guide créé pour le moment.</p>
          ) : !loading ? (
            <div className="guides-grid">
              {guides.map((guide) => (
                <div key={guide.id} className="guide-card">
                  <div className="guide-qrcode">
                    <img
                      src={qrDataUrls[guide.id] || ''}
                      alt={`QR ${guide.label}`}
                      style={{ display: qrDataUrls[guide.id] ? 'block' : 'none' }}
                    />
                  </div>

                  <div className="guide-info">
                    <h3>{guide.label}</h3>
                    <p className="guide-slug">{guide.id}</p>
                    <a
                      href={`${baseUrl}/guides/${guide.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="guide-link"
                      title={`${baseUrl}/guides/${guide.id}`}
                    >
                      Ouvrir le guide
                    </a>
                  </div>

                  <div className="guide-languages">
                    {LANGUAGES.map((lang) => {
                      const hasFile = guide.languages?.[lang];
                      const pdfPublicUrl = `${baseUrl}/docs/${guide.id}/${lang}.pdf`;

                      return (
                        <div key={lang} className="language-item">
                          <span className={`lang-badge ${hasFile ? 'active' : ''}`}>{lang.toUpperCase()}</span>
                          <label className="upload-label">
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) => handleUploadPDF(e, guide.id, lang)}
                              disabled={uploadingGuideId === guide.id && uploadingLang === lang}
                              style={{ display: 'none' }}
                            />
                            <span className="upload-button">
                              {uploadingGuideId === guide.id && uploadingLang === lang
                                ? '⏳'
                                : hasFile
                                  ? 'Remplacer PDF'
                                  : 'Télécharger PDF'}
                            </span>
                          </label>
                          {hasFile && (
                            <>
                              <a
                                href={pdfPublicUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pdf-path"
                                title={pdfPublicUrl}
                              >
                                {pdfPublicUrl}
                              </a>
                              <button
                                type="button"
                                className="upload-delete-button"
                                onClick={() => handleDeletePDF(guide.id, lang)}
                              >
                                Supprimer PDF
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="guide-actions">
                    <button className="btn-secondary" onClick={() => handleRegenerateQR(guide.id)}>
                      🔄 Régénérer QR
                    </button>
                    <button className="btn-secondary" onClick={() => handleDownloadQR(guide.id)}>
                      📥 Télécharger QR Code
                    </button>
                    <button className="btn-danger" onClick={() => handleDeleteGuide(guide.id)}>
                      🗑️ Supprimer le guide
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {isCreateModalOpen && (
            <div className="modal-backdrop" onClick={() => setIsCreateModalOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Nouveau guide</h2>
                <form onSubmit={handleCreateGuide}>
                  <div className="form-group">
                    <label>Identifiant (slug)</label>
                    <input
                      type="text"
                      placeholder="machine-a-laver"
                      value={newGuide.slug}
                      onChange={(e) => setNewGuide({ ...newGuide, slug: sanitizeSlug(e.target.value) })}
                    />
                    <p className="slug-help">
                      Format slug: minuscules, chiffres et tirets uniquement (pas d'espace, pas d'accent). Exemple: machine-a-laver
                    </p>
                  </div>

                  <div className="form-group">
                    <label>Nom du guide</label>
                    <input
                      type="text"
                      placeholder="Machine à laver"
                      value={newGuide.label}
                      onChange={(e) => setNewGuide({ ...newGuide, label: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Sélectionner les langues</label>
                    <div className="languages-checkboxes">
                      {LANGUAGES.map((lang) => (
                        <label key={lang} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={newGuide.languages[lang]}
                            onChange={(e) =>
                              setNewGuide({
                                ...newGuide,
                                languages: { ...newGuide.languages, [lang]: e.target.checked }
                              })
                            }
                          />
                          {LANGUAGE_NAMES_FR[lang]}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button type="submit" className="btn-primary">
                      Créer le guide
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setIsCreateModalOpen(false)}>
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {dialog.isOpen && (
            <div className="modal-backdrop" onClick={closeDialog}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>{dialog.title}</h2>
                <p className="dialog-message">{dialog.message}</p>
                <div className="modal-actions">
                  {dialog.onConfirm && (
                    <button
                      type="button"
                      className={dialog.dangerConfirm ? 'btn-danger' : 'btn-primary'}
                      onClick={handleDialogConfirm}
                    >
                      {dialog.confirmLabel}
                    </button>
                  )}
                  <button type="button" className="btn-secondary" onClick={closeDialog}>
                    {dialog.cancelLabel}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GestionInfos;
